import {
  type ChipProps,
  Stack,
  Tooltip,
  type TooltipProps,
  styled,
  tooltipClasses,
} from "@mui/material"
import type { CSSProperties, FC } from "react"
import { MoreHorizontal } from "react-feather"

/**
 * Interface defining the props for the ChipList component
 * @template T Type extending MUI ChipProps to ensure all chips have required properties
 */
export interface ChipListProps<T extends ChipProps> {
  /** Array of chip data to be displayed */
  chips: Array<Partial<Omit<T, "label">> & Pick<T, "label">>
  /** Maximum number of chips to display before showing the "more" button with tooltip */
  maxChipsCount?: number
  /** Spacing between chips in the list */
  gap?: number
  /** The Chip component to render for each item */
  ChipElement: FC<T>
  /** Default props to apply to all chips */
  defaultChipProps?: Omit<T, "label">
  /** Chip list container css style */
  containerStyle?: CSSProperties
}

/**
 * ChipList component - Displays a collection of chips with a "more" button when exceeding maximum count
 *
 * @template T Type extending ChipProps for the chip component
 * @param chips - Array of chip data to display
 * @param maxChipsCount - Maximum number of chips to show before using the "more" button (defaults to showing all)
 * @param gap - Spacing between chips (defaults to 1)
 * @param ChipElement - The component used to render each chip
 * @param defaultChipProps - Default props applied to all chips
 * @param containerStyle - Added style to the container
 * @returns A responsive list of chips with optional "more" tooltip
 */
export const ChipList = <T extends ChipProps>({
  chips,
  maxChipsCount = chips.length,
  gap = 1,
  ChipElement,
  defaultChipProps,
  containerStyle,
}: ChipListProps<T>) => {
  // Split chips into visible ones and those hidden behind the "more" button
  const visibleChips = chips.slice(0, maxChipsCount)
  const hiddenChips = chips.slice(maxChipsCount)

  return (
    <Stack
      direction="row"
      gap={gap}
      flexWrap="wrap"
      width="100%"
      style={containerStyle}
    >
      {/* Render visible chips */}
      {visibleChips.map(({ label, ...ownProps }) => {
        return (
          <ChipElement
            {...(defaultChipProps as T)}
            {...ownProps}
            label={label}
            key={label}
          />
        )
      })}
      {/* Render "more" button with tooltip if there are hidden chips */}
      {hiddenChips.length > 0 && (
        <CustomTooltip
          arrow
          title={
            <Stack spacing={1}>
              {/* Render hidden chips inside the tooltip */}
              {hiddenChips.map(({ label, ...ownProps }) => (
                <ChipElement
                  {...(defaultChipProps as T)}
                  {...ownProps}
                  label={label}
                  key={label}
                />
              ))}
            </Stack>
          }
        >
          {/* "More" button chip with horizontal dots icon */}
          <ChipElement
            {...(defaultChipProps as T)}
            label={<MoreHorizontal size={11} />}
            sx={{
              ...(defaultChipProps?.sx ?? {}),
              cursor: "pointer",
              "& .MuiChip-label": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        </CustomTooltip>
      )}
    </Stack>
  )
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    padding: theme.spacing(1.2),
  },
}))
